import type { ComponentProps } from 'react';

import { cn, cva } from '../../shared';
import { fieldSurface } from './field-surface';

export const textareaVariants = cva([
  ...fieldSurface,
  'px-input py-input text-body',
  // Vertical only. A horizontally resizable textarea escapes whatever laid it
  // out, and no layout in the product survives that.
  'resize-y',
]);

export interface TextareaProps extends ComponentProps<'textarea'> {
  /**
   * Grows with its content instead of scrolling. Progressive enhancement:
   * `field-sizing` is not in every engine yet, and where it is missing `rows`
   * still governs, so the control is short rather than broken.
   */
  autoGrow?: boolean;
}

/**
 * A bare styled `<textarea>`, on the same surface as `Input`, so a form
 * library's field object spreads onto it directly.
 *
 * Height comes from the native `rows` attribute rather than a size variant:
 * how many lines a field should show is a property of the content it holds,
 * not of a scale.
 */
export function Textarea({ autoGrow = false, className, rows = 3, ...props }: TextareaProps) {
  return (
    <textarea
      rows={rows}
      className={cn(textareaVariants(), autoGrow && 'field-sizing-content', className)}
      {...props}
    />
  );
}
